/**
 * RWBR — THE CALENDAR. Thirty-six races, and which of them is a playoff race.
 *
 * D-004 fixes the season at **36 races**, because every one of Sagliano's
 * financial tables divides by 36 (36 Mb weekly race cost x 36 races = 1,296
 * Mb/yr, exactly as printed). D-012 puts the playoff *inside* those 36 rather
 * than adding to them, so the money math needs no adjustment at all:
 *
 *   | Round          | Races | Championship field |
 *   |----------------|-------|--------------------|
 *   | Regular season | 1-26  | all                |
 *   | Round of 16    | 27-29 | 16                 |
 *   | Round of 12    | 30-32 | 12                 |
 *   | Round of 8     | 33-35 | 8                  |
 *   | Championship   | 36    | 4                  |
 *
 * "Field" is the CHAMPIONSHIP field, never the starting field. Everybody who
 * qualifies still races every week — RWEG/p.4 rounds the grid out to 43 in the
 * playoff exactly as it does in March. What shrinks is the list of drivers who
 * can still win the title.
 *
 * WHERE THE RACES COME FROM. `src/data/track-cards.json` holds 31 published
 * PLAAY track cards, and fourteen of them print a SECOND named race at a
 * different length (N-005: Charlotte is the Coca-Cola 600 at 60 turns *and* the
 * Bank of America 500 at 50). That is 45 named races, and the schedule is a
 * selection of 36 of them. A track card is a venue; a named race is an event.
 *
 * PURITY. Deterministic from one seed, reads nothing but the imported JSON,
 * mutates nothing.
 */

import { makeRng } from './dice.ts';
import trackCardsJson from '../data/track-cards.json';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_SEASON_LENGTH = 'D-004 — Sagliano\'s 36-race season (every finance table divides by 36)';
export const CITE_PLAYOFF_SHAPE = 'D-012 — 26 regular-season races + a 10-race playoff, inside 36';
export const CITE_TWO_RACES_ONE_TRACK =
  'N-005 — "14 cards define two different races at different lengths"; track -> many races';
export const CITE_DOUBLE_POINTS = 'D-023 — "Two double-points races per season"';

// ---------------------------------------------------------------------------
// Season shape
// ---------------------------------------------------------------------------

export const SEASON_RACES = 36;
export const REGULAR_SEASON_RACES = 26;

export type PlayoffRound =
  | 'regular'
  | 'round-of-16'
  | 'round-of-12'
  | 'round-of-8'
  | 'championship';

export interface RoundSpec {
  round: PlayoffRound;
  /** Human label for the booth. */
  label: string;
  /** Inclusive 1-based race numbers. */
  firstRace: number;
  lastRace: number;
  /**
   * How many drivers are still eligible for the championship during this round.
   * `null` in the regular season, where everybody is.
   */
  championshipField: number | null;
  /** How many survive into the next round. `null` for the championship race. */
  advancing: number | null;
}

/** D-012, verbatim. The one place the shape of a season is written down. */
export const ROUNDS: readonly RoundSpec[] = [
  { round: 'regular', label: 'Regular Season', firstRace: 1, lastRace: 26, championshipField: null, advancing: 16 },
  { round: 'round-of-16', label: 'Round of 16', firstRace: 27, lastRace: 29, championshipField: 16, advancing: 12 },
  { round: 'round-of-12', label: 'Round of 12', firstRace: 30, lastRace: 32, championshipField: 12, advancing: 8 },
  { round: 'round-of-8', label: 'Round of 8', firstRace: 33, lastRace: 35, championshipField: 8, advancing: 4 },
  { round: 'championship', label: 'Championship', firstRace: 36, lastRace: 36, championshipField: 4, advancing: null },
];

/** The playoff rounds, in order. The regular season is not one of them. */
export const PLAYOFF_ROUNDS: readonly RoundSpec[] = ROUNDS.filter((r) => r.round !== 'regular');

export function roundSpec(round: PlayoffRound): RoundSpec {
  const spec = ROUNDS.find((r) => r.round === round);
  if (!spec) throw new RangeError(`no such round: ${round}`);
  return spec;
}

/** Which round race `n` belongs to. */
export function roundForRace(raceNumber: number): PlayoffRound {
  const spec = ROUNDS.find((r) => raceNumber >= r.firstRace && raceNumber <= r.lastRace);
  if (!spec) {
    throw new RangeError(`race ${raceNumber} is outside a ${SEASON_RACES}-race season`);
  }
  return spec.round;
}

/** The championship field for a race number: null in the regular season. */
export function championshipFieldFor(raceNumber: number): number | null {
  return roundSpec(roundForRace(raceNumber)).championshipField;
}

/** Is this one of the ten playoff races (27-36)? */
export function isPlayoffRace(raceNumber: number): boolean {
  return raceNumber > REGULAR_SEASON_RACES;
}

// ---------------------------------------------------------------------------
// The 45 named races
// ---------------------------------------------------------------------------

export type TrackCategory = 'Road Course' | 'Super Speedway' | 'Intermediate Track' | 'Short Track';

export const TRACK_CATEGORIES: readonly TrackCategory[] = [
  'Road Course',
  'Super Speedway',
  'Intermediate Track',
  'Short Track',
];

/**
 * The extra named event a track card prints under its main one. Present on 14
 * of the 31 cards (N-005) and carrying its own length in turns.
 */
interface AdditionalRaceJson {
  raceName: string;
  turns: number;
}

interface TrackCardJson {
  id: string;
  /**
   * THE PHYSICAL RACETRACK — D-EZ-75.
   *
   * Howard, 21 Aug 2026, adding the 2025 card set: *"they can be made available
   * as long as theyre not duplicative meaning dont use Daytona 2025 and Daytona
   * 2014 and think that those are 2 separate tracks."*
   *
   * `daytona-2000s`, `daytona-2014` and `daytona-2025` are three CARDS of one
   * VENUE. A calendar built from card ids would send the field to Daytona Beach
   * six times in a year and believe it had been twice.
   */
  venue?: string;
  /** Which season's card set this is from. 2012, 2014, 2025. */
  cardYear?: number;
  name: string;
  category: string;
  location?: string | null;
  raceName?: string | null;
  turns: number;
  additionalRaces?: AdditionalRaceJson[];
  /**
   * THE SLOTS THIS CARD'S RACES ACTUALLY OCCUPIED — D-EZ-76.
   *
   * The 2025 cards print their real race numbers, and 27 cards carrying 36 race
   * numbers between them ARE the 2025 calendar. `printedSchedule` rebuilds it
   * exactly rather than shuffling, which is the whole point of a scenario: a
   * player who starts in 2025 opens at Daytona and finishes at Phoenix, the way
   * it happened.
   *
   * Aligned with the events by index: `raceNumbers[0]` is the headline race,
   * `raceNumbers[i + 1]` is `additionalRaces[i]`.
   */
  raceNumbers?: number[];
}

const TRACK_JSON = trackCardsJson as unknown as TrackCardJson[];

/** One event on one track card. 45 of these exist. */
export interface NamedRace {
  /** `${trackId}#${variant}` — stable, and unique across the whole calendar. */
  raceKey: string;
  trackId: string;
  /** The physical racetrack, independent of which year's card this is. */
  venue: string;
  /** Which card set this race came off. */
  cardYear: number;
  trackName: string;
  location: string | null;
  category: TrackCategory;
  /** The event's own name, as printed: "Coca-Cola 600", "Bank of America 500". */
  raceName: string;
  /** This event's own length. The second race at a track is usually shorter. */
  turns: number;
  /** 0 = the card's headline race, 1 = the card's `additionalRaces[0]`. */
  variant: number;
}

/**
 * Every named race on every published track card, card order then variant
 * order. Frozen: this is data, and a caller that edits it edits the calendar
 * for every season in the process.
 */
export const NAMED_RACES: readonly NamedRace[] = Object.freeze(
  TRACK_JSON.flatMap((card) => {
    const category = card.category as TrackCategory;
    if (!TRACK_CATEGORIES.includes(category)) {
      throw new Error(`track ${card.id} has an unknown category "${card.category}"`);
    }
    const venue = card.venue ?? card.id;
    const cardYear = card.cardYear ?? 0;
    const head: NamedRace = {
      raceKey: `${card.id}#0`,
      trackId: card.id,
      venue,
      cardYear,
      trackName: card.name,
      location: card.location ?? null,
      category,
      raceName: card.raceName ?? card.name,
      turns: card.turns,
      variant: 0,
    };
    const extras = (card.additionalRaces ?? []).map((extra, i) => ({
      raceKey: `${card.id}#${i + 1}`,
      trackId: card.id,
      venue,
      cardYear,
      trackName: card.name,
      location: card.location ?? null,
      category,
      raceName: extra.raceName,
      turns: extra.turns,
      variant: i + 1,
    }));
    return [head, ...extras];
  }),
);

/** Every distinct track id, in card order. */
export const TRACK_IDS: readonly string[] = Object.freeze([...new Set(NAMED_RACES.map((r) => r.trackId))]);

/** Every distinct physical racetrack, in card order. */
export const VENUES: readonly string[] = Object.freeze([...new Set(NAMED_RACES.map((r) => r.venue))]);

/** The venue a track card belongs to. */
export const venueOf = (trackId: string): string =>
  NAMED_RACES.find((r) => r.trackId === trackId)?.venue ?? trackId;

/** Every card printed for one venue, newest card set first. */
export const cardsAtVenue = (venue: string): string[] => [
  ...new Set(
    NAMED_RACES.filter((r) => r.venue === venue)
      .slice()
      .sort((a, b) => b.cardYear - a.cardYear)
      .map((r) => r.trackId),
  ),
];

/** The named races a track hosts, in variant order. */
export function racesAtTrack(trackId: string): NamedRace[] {
  return NAMED_RACES.filter((r) => r.trackId === trackId);
}

// ---------------------------------------------------------------------------
// A scheduled race
// ---------------------------------------------------------------------------

export interface ScheduledRace extends NamedRace {
  /** 1-36. */
  raceNumber: number;
  round: PlayoffRound;
  /** 1-based position inside the round. */
  raceInRound: number;
  /**
   * D-023 — the base finishing points double here. The win bonus stays 3 and
   * the two turn points stay 1 and 1.
   */
  doublePoints: boolean;
  /** The championship field for this race's round; null in the regular season. */
  championshipField: number | null;
}

export interface SeasonSchedule {
  seed: number;
  races: readonly ScheduledRace[];
  /** The two race numbers D-023 doubles. */
  doublePointsRaces: readonly number[];
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface ScheduleOptions {
  /**
   * How many tracks run BOTH of their named races in one season.
   *
   * ⚠ A FLOOR NOW, NOT A FIXED COUNT — D-EZ-75. It used to be exact, and
   * `buildSchedule` asserted `tracks + doubleHeaders === 36`, which meant the
   * library could never grow by a single card without every caller breaking.
   * Adding the 2025 set took it from 31 cards to 58 and the whole app failed to
   * load. The calendar now derives the count from how many VENUES it is using,
   * and this is the minimum it will settle for.
   */
  doubleHeaderTracks?: number;
  /**
   * Prefer this card set where a venue prints more than one card.
   *
   * Daytona has three cards — 2000s, 2014, 2025 — and a season visits Daytona
   * Beach once or twice, not six times. Exactly one card per venue is chosen;
   * this says which. Absent, the seed picks, so a calendar stays a pure
   * function of its seed.
   */
  cardYear?: number;
  /**
   * Use exactly these track cards and no others, in this order of preference.
   *
   * The escape hatch a preset season needs: the 2025 scenario hands over its
   * own 27 cards and gets the real 2025 calendar rather than a shuffle.
   */
  cards?: readonly string[];
  /**
   * The slots the mid-season double-points race is chosen from. The longest
   * race in the window gets it — the crown jewel of the summer.
   */
  midSeasonDoubleWindow?: [number, number];
}

const DEFAULT_DOUBLE_HEADERS = 5;
const DEFAULT_MID_WINDOW: [number, number] = [11, 16];

// ---------------------------------------------------------------------------
// Building the calendar
// ---------------------------------------------------------------------------

/**
 * Build a 36-race calendar from the 45 named races.
 *
 * THE SELECTION. Every one of the 31 tracks appears at least once — a season
 * that skipped Darlington would not be a season. That leaves five races to
 * find, and they come from five of the fourteen tracks that print a second
 * named event. Each of those five runs BOTH its races: one in the regular
 * season, one in the playoff. Nothing else in the calendar can repeat, because
 * a named race is an event and an event happens once a year.
 *
 * THE ORDERING. Categories are dealt out by their own ideal spacing — three
 * road courses across 36 weeks want twelve weeks between them, nineteen
 * intermediates want under two — and at every slot the calendar takes whichever
 * remaining race is furthest *behind* its own schedule. That is the standard
 * smooth-interleave construction, and it distributes road courses and short
 * tracks rather than clustering them without ever needing a repair pass. A
 * track is never allowed to follow itself, which only ever bites at the seam
 * between race 26 and race 27.
 */
export function buildSchedule(seed: number, options: ScheduleOptions = {}): SeasonSchedule {
  const minDoubles = options.doubleHeaderTracks ?? DEFAULT_DOUBLE_HEADERS;
  const midWindow = options.midSeasonDoubleWindow ?? DEFAULT_MID_WINDOW;
  const playoffRaces = SEASON_RACES - REGULAR_SEASON_RACES;

  if (!Number.isInteger(minDoubles) || minDoubles < 0) {
    throw new RangeError(`double-header tracks must be a non-negative integer, got ${minDoubles}`);
  }

  const rng = makeRng((seed ^ 0x5ca1ab1e) >>> 0);
  const shuffled = <T>(xs: readonly T[]): T[] => {
    const out = [...xs];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  };

  /*
    ═══════════════════════════════════════════════════════════════════════════
    ONE CARD PER RACETRACK — D-EZ-75.

    Howard, adding the 2025 set: *"they can be made available as long as theyre
    not duplicative meaning dont use Daytona 2025 and Daytona 2014 and think
    that those are 2 separate tracks."*

    The library is now 58 cards over 30 venues. Daytona has three; so do
    Talladega, Bristol, Martinsville, Darlington, Texas, New Hampshire and
    Watkins Glen. Built from card ids, a calendar would treat each of those as a
    separate racetrack and send the field to Bristol six times in a year.

    So a season picks its CARD SET first — exactly one card per venue — and
    every line below then runs on that set exactly as it always did. Which card
    is a deterministic function of the seed unless the caller asks for a year
    (`cardYear`) or pins the set outright (`cards`, which is how the 2025
    scenario gets the real 2025 calendar).
    ═══════════════════════════════════════════════════════════════════════════
  */
  const pinned = options.cards ? new Set(options.cards) : null;
  const venuePool = pinned
    ? [...new Set(NAMED_RACES.filter((r) => pinned.has(r.trackId)).map((r) => r.venue))]
    : [...VENUES];

  const cardForVenue = (venue: string): string => {
    const cards = cardsAtVenue(venue).filter((id) => !pinned || pinned.has(id));
    if (cards.length === 0) throw new RangeError(`no usable card for venue "${venue}"`);
    if (cards.length === 1) return cards[0];
    if (options.cardYear !== undefined) {
      const wanted = cards.find(
        (id) => NAMED_RACES.find((r) => r.trackId === id)?.cardYear === options.cardYear,
      );
      if (wanted) return wanted;
    }
    return cards[Math.floor(rng() * cards.length)];
  };

  /*
    HOW MANY RACETRACKS THE YEAR VISITS.

    A season is 36 races however big the library gets. Use as many distinct
    venues as will fit while still leaving room for the double-headers, and let
    the double-header count absorb the remainder — so 30 venues means six
    tracks run both their races, and a library that grows to 40 venues means
    thirty-one venues and the minimum five. The alternative, which is what this
    used to do, is to hard-assert a number and break on the next card anybody
    adds.
  */
  const venuesUsed = Math.min(venuePool.length, SEASON_RACES - minDoubles);
  const doubleHeaders = SEASON_RACES - venuesUsed;
  if (doubleHeaders > playoffRaces) {
    throw new RangeError(
      `${doubleHeaders} double-headers cannot each put a race in the ${playoffRaces}-race playoff — ` +
        `the library has only ${venuePool.length} venues and a season is ${SEASON_RACES} races`,
    );
  }

  const chosenVenues = venuesUsed === venuePool.length ? venuePool : shuffled(venuePool).slice(0, venuesUsed);
  const cardOf = new Map(chosenVenues.map((v) => [v, cardForVenue(v)]));
  const racesAtTrack = (trackId: string): NamedRace[] =>
    NAMED_RACES.filter((r) => r.trackId === trackId);

  /*
    ═══════════════════════════════════════════════════════════════════════════
    ⚠ THE CARD CHOICE HAS TO KNOW THE SEASON NEEDS DOUBLE-HEADERS.

    Measured 21 Aug 2026, after Howard asked why he could not see the 2025
    tracks: **1.2% of seeds threw and no league could be started on them** —
    seeds 87, 237, 260, 384 and 391 among the first 500. The exception was
    honest ("only 5 tracks print a second named race; cannot schedule 6") and
    the cause was one step upstream.

    A season visits all 30 venues, so six of them must run BOTH their races.
    Sixteen venues can supply a card that prints a second race, but only THREE
    do it on every card they have — the other thirteen print two races on one
    card and one on another (Talladega 2025 and 2014 print two; Talladega 2000s
    prints one). `cardForVenue` chose blind, so a seed that happened to pick the
    single-race card at eleven of those thirteen left the calendar one
    double-header short and the whole thing fell over.

    This is the repair, and it is deliberately the SMALLEST one: only when the
    chosen set is short does anything change, so 98.8% of seeds build exactly
    the calendar they built before — including every seed anybody has already
    played. ⚠ THE RNG IS ONLY TOUCHED INSIDE THE `if`, which is what makes that
    true: a shuffle drawn unconditionally would shift every later roll and
    repaint every season in the game.
  */
  const twoRaceCardAt = (venue: string): string | undefined =>
    cardsAtVenue(venue)
      .filter((id) => !pinned || pinned.has(id))
      .find((id) => racesAtTrack(id).length > 1);

  let canDouble = [...cardOf.values()].filter((id) => racesAtTrack(id).length > 1).length;
  if (canDouble < doubleHeaders) {
    // Newest card first (`cardsAtVenue` sorts that way), so the upgrade also
    // leans toward the most recent set rather than the oldest one on the shelf.
    for (const venue of shuffled(chosenVenues)) {
      if (canDouble >= doubleHeaders) break;
      const current = cardOf.get(venue) as string;
      if (racesAtTrack(current).length > 1) continue;
      const upgrade = twoRaceCardAt(venue);
      if (!upgrade) continue;
      cardOf.set(venue, upgrade);
      canDouble++;
    }
  }

  const TRACK_IDS = [...cardOf.values()];

  // ---- pick the double-headers ------------------------------------------
  const twoRaceTracks = TRACK_IDS.filter((id) => racesAtTrack(id).length > 1);
  if (twoRaceTracks.length < doubleHeaders) {
    throw new RangeError(
      `only ${twoRaceTracks.length} of the ${TRACK_IDS.length} chosen cards print a second named race; ` +
        `cannot schedule ${doubleHeaders} double-headers. The library needs more venues that print two ` +
        `races, not a different seed.`,
    );
  }
  const doubleTrackIds = new Set(shuffled(twoRaceTracks).slice(0, doubleHeaders));

  // ---- pick which tracks the playoff visits ------------------------------
  // A double-header track always contributes one race to each half, so the
  // playoff needs `playoffRaces - doubleHeaders` more tracks, drawn from the
  // ones running a single event.
  //
  // The playoff is drawn to CATEGORY QUOTA, not at random. Three road courses
  // exist in the whole game; an unquota'd draw can put two of them inside a
  // ten-race playoff, and no amount of clever ordering afterwards can spread
  // two road courses across ten weeks. Each category may hold at most its
  // proportional share of the playoff, rounded up.
  const singleTrackIds = TRACK_IDS.filter((id) => !doubleTrackIds.has(id));
  const categoryQuota = new Map<TrackCategory, number>();
  for (const category of TRACK_CATEGORIES) {
    const share = NAMED_RACES.filter((r) => r.category === category).length / NAMED_RACES.length;
    categoryQuota.set(category, Math.ceil(playoffRaces * share));
  }

  const categoryOf = (trackId: string): TrackCategory => racesAtTrack(trackId)[0].category;
  const playoffCategoryCount = new Map<TrackCategory, number>();
  const bump = (category: TrackCategory) =>
    playoffCategoryCount.set(category, (playoffCategoryCount.get(category) ?? 0) + 1);
  for (const trackId of doubleTrackIds) bump(categoryOf(trackId));

  const playoffOnly = new Set<string>();
  const wanted = playoffRaces - doubleHeaders;
  const shuffledSingles = shuffled(singleTrackIds);
  for (const pass of [true, false]) {
    for (const trackId of shuffledSingles) {
      if (playoffOnly.size >= wanted) break;
      if (playoffOnly.has(trackId)) continue;
      const category = categoryOf(trackId);
      // First pass respects the quota; the second pass exists only so a
      // pathological quota can never leave the playoff short of races.
      if (pass && (playoffCategoryCount.get(category) ?? 0) >= (categoryQuota.get(category) ?? 0)) continue;
      playoffOnly.add(trackId);
      bump(category);
    }
  }

  const regularPool: NamedRace[] = [];
  const playoffPool: NamedRace[] = [];

  for (const trackId of TRACK_IDS) {
    const races = racesAtTrack(trackId);
    if (doubleTrackIds.has(trackId)) {
      // Both events run. Which one opens the year is a coin flip, so the same
      // track does not always host its headline race in the spring.
      const swap = rng() < 0.5;
      const [first, second] = swap ? [races[1], races[0]] : [races[0], races[1]];
      regularPool.push(first);
      playoffPool.push(second);
    } else {
      // One event runs. If the card prints two, pick one.
      const chosen = races[Math.floor(rng() * races.length)];
      (playoffOnly.has(trackId) ? playoffPool : regularPool).push(chosen);
    }
  }

  if (regularPool.length !== REGULAR_SEASON_RACES || playoffPool.length !== playoffRaces) {
    throw new Error(
      `calendar selection produced ${regularPool.length} regular-season and ${playoffPool.length} playoff races`,
    );
  }

  // ---- order them --------------------------------------------------------
  //
  // Each half is smoothed against ITS OWN composition. A category's ideal gap
  // inside the regular season is 26/n, not 36/n — measure a ten-race playoff
  // against a 36-race yardstick and its two short tracks land in consecutive
  // weeks because neither ever looks overdue.
  //
  // `lastSeen` carries across the seam in the second half's own coordinates
  // (a category last raced in week 24 enters the playoff at -2), so the join
  // between race 26 and race 27 is smoothed like any other.
  const tieBreak = new Map<string, number>();
  shuffled([...regularPool, ...playoffPool]).forEach((race, i) => tieBreak.set(race.raceKey, i));

  const idealGapFor = (pool: readonly NamedRace[]): Map<TrackCategory, number> => {
    const counts = new Map<TrackCategory, number>();
    for (const race of pool) counts.set(race.category, (counts.get(race.category) ?? 0) + 1);
    const gaps = new Map<TrackCategory, number>();
    for (const [category, n] of counts) gaps.set(category, pool.length / n);
    return gaps;
  };

  const ordered: NamedRace[] = [];
  const lastSeen = new Map<TrackCategory, number>();
  let lastTrackId: string | null = null;

  const layOut = (pool: readonly NamedRace[]): void => {
    const idealGap = idealGapFor(pool);
    const remaining = shuffled(pool);
    for (let slot = 1; slot <= pool.length; slot++) {
      // "No track twice in a row" — the only place this can bite is the seam,
      // where a double-header track's second race could follow its first.
      let candidates = remaining.filter((r) => r.trackId !== lastTrackId);
      if (candidates.length === 0) candidates = remaining;

      let best = candidates[0];
      let bestScore = -Infinity;
      for (const race of candidates) {
        const gap = slot - (lastSeen.get(race.category) ?? 0);
        const score = gap / (idealGap.get(race.category) ?? 1);
        const better =
          score > bestScore + 1e-9 ||
          (Math.abs(score - bestScore) <= 1e-9 &&
            (tieBreak.get(race.raceKey) ?? 0) < (tieBreak.get(best.raceKey) ?? 0));
        if (better) {
          best = race;
          bestScore = score;
        }
      }

      ordered.push(best);
      remaining.splice(remaining.indexOf(best), 1);
      lastSeen.set(best.category, slot);
      lastTrackId = best.trackId;
    }
    // Re-base every category into the next half's coordinates.
    for (const [category, slot] of [...lastSeen]) lastSeen.set(category, slot - pool.length);
  };

  layOut(regularPool);
  layOut(playoffPool);

  // ---- the two double-points races --------------------------------------
  //
  // AMBIGUITY: D-023 says "two double-points races per season" and does not say
  // which. Placed the way a real calendar places its crown jewels — the longest
  // race of the summer, and the finale. The mid-season one is the longest race
  // inside the window (Charlotte's 60-turn Coca-Cola 600 when it lands there),
  // ties going to the earlier week; the playoff one is race 36, where four
  // drivers are racing for the title and nothing else is left to weight.
  const [windowStart, windowEnd] = midWindow;
  let midRace = windowStart;
  for (let slot = windowStart; slot <= Math.min(windowEnd, REGULAR_SEASON_RACES); slot++) {
    if (ordered[slot - 1].turns > ordered[midRace - 1].turns) midRace = slot;
  }
  const doublePointsRaces = [midRace, SEASON_RACES];

  const races: ScheduledRace[] = ordered.map((race, i) => {
    const raceNumber = i + 1;
    const spec = roundSpec(roundForRace(raceNumber));
    return {
      ...race,
      raceNumber,
      round: spec.round,
      raceInRound: raceNumber - spec.firstRace + 1,
      doublePoints: doublePointsRaces.includes(raceNumber),
      championshipField: spec.championshipField,
    };
  });

  return { seed, races, doublePointsRaces };
}

// ---------------------------------------------------------------------------
// A calendar that was not invented — D-EZ-76
// ---------------------------------------------------------------------------

/** Every card year that prints its own race numbers, and can therefore be run as it stood. */
export const PRINTED_YEARS: readonly number[] = Object.freeze([
  ...new Set(TRACK_JSON.filter((c) => (c.raceNumbers?.length ?? 0) > 0).map((c) => c.cardYear ?? 0)),
]);

/** The track cards a printed year is made of, in calendar order. */
export function printedCards(cardYear: number): string[] {
  return TRACK_JSON.filter((c) => c.cardYear === cardYear && (c.raceNumbers?.length ?? 0) > 0)
    .slice()
    .sort((a, b) => (a.raceNumbers?.[0] ?? 0) - (b.raceNumbers?.[0] ?? 0))
    .map((c) => c.id);
}

/**
 * THE CALENDAR AS IT WAS PRINTED, not as a seed would have dealt it.
 *
 * `buildSchedule` is a generator: it picks venues, spaces the categories and
 * hands you a plausible year. This does none of that, because for a year that
 * actually happened there is nothing to decide — every card names the slots its
 * races ran in, and the only work is checking that they add up.
 *
 * ⚠ IT VALIDATES RATHER THAN REPAIRS. A missing slot or a duplicate throws
 * here, at start-up, naming the number. The alternative is a season that quietly
 * runs 35 races or visits Kansas twice in October, and the place that failure
 * would surface is the championship.
 *
 * `seed` still matters and is still stored: it is every die of the season. Only
 * the calendar is fixed.
 */
export function printedSchedule(seed: number, cardYear: number, options: { midSeasonDoubleWindow?: [number, number] } = {}): SeasonSchedule {
  const cards = TRACK_JSON.filter((c) => c.cardYear === cardYear && (c.raceNumbers?.length ?? 0) > 0);
  if (cards.length === 0) {
    throw new RangeError(`no track card from ${cardYear} prints its race numbers`);
  }

  const bySlot = new Map<number, NamedRace>();
  for (const card of cards) {
    const events = NAMED_RACES.filter((r) => r.trackId === card.id).sort((a, b) => a.variant - b.variant);
    const slots = card.raceNumbers ?? [];
    if (slots.length !== events.length) {
      throw new Error(
        `${card.id} prints ${slots.length} race numbers but defines ${events.length} named races`,
      );
    }
    slots.forEach((slot, i) => {
      if (!Number.isInteger(slot) || slot < 1 || slot > SEASON_RACES) {
        throw new RangeError(`${card.id} claims race ${slot}, outside a ${SEASON_RACES}-race season`);
      }
      const taken = bySlot.get(slot);
      if (taken) throw new Error(`race ${slot} is claimed by both ${taken.trackId} and ${card.id}`);
      bySlot.set(slot, events[i]);
    });
  }

  const ordered: NamedRace[] = [];
  for (let slot = 1; slot <= SEASON_RACES; slot++) {
    const race = bySlot.get(slot);
    if (!race) throw new Error(`the ${cardYear} calendar has no race ${slot}`);
    ordered.push(race);
  }

  // The same D-023 rule `buildSchedule` uses: the longest race of the summer,
  // and the finale. In 2025 that is the Coca-Cola 600 and the title race at
  // Phoenix, which is what a real calendar would have chosen anyway.
  const [windowStart, windowEnd] = options.midSeasonDoubleWindow ?? DEFAULT_MID_WINDOW;
  let midRace = windowStart;
  for (let slot = windowStart; slot <= Math.min(windowEnd, REGULAR_SEASON_RACES); slot++) {
    if (ordered[slot - 1].turns > ordered[midRace - 1].turns) midRace = slot;
  }
  const doublePointsRaces = [midRace, SEASON_RACES];

  const races: ScheduledRace[] = ordered.map((race, i) => {
    const raceNumber = i + 1;
    const spec = roundSpec(roundForRace(raceNumber));
    return {
      ...race,
      raceNumber,
      round: spec.round,
      raceInRound: raceNumber - spec.firstRace + 1,
      doublePoints: doublePointsRaces.includes(raceNumber),
      championshipField: spec.championshipField,
    };
  });

  return { seed, races, doublePointsRaces };
}

// ---------------------------------------------------------------------------
// Reading a calendar back
// ---------------------------------------------------------------------------

export function raceAt(schedule: SeasonSchedule, raceNumber: number): ScheduledRace {
  const race = schedule.races[raceNumber - 1];
  if (!race) throw new RangeError(`race ${raceNumber} is not on this schedule`);
  return race;
}

export function racesInRound(schedule: SeasonSchedule, round: PlayoffRound): ScheduledRace[] {
  return schedule.races.filter((r) => r.round === round);
}

/** A one-line calendar entry for the booth. */
export function scheduleLine(race: ScheduledRace): string {
  const tags = [
    race.round === 'regular' ? null : roundSpec(race.round).label,
    race.doublePoints ? 'DOUBLE POINTS' : null,
  ].filter(Boolean);
  return (
    `${String(race.raceNumber).padStart(2)}  ${race.raceName} — ${race.trackName} ` +
    `(${race.category}, ${race.turns} turns)${tags.length ? '  [' + tags.join(' · ') + ']' : ''}`
  );
}
